import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FormattedMessage } from '../../util/reactIntl';
import { getAllTopics, getTopicById, getTopicByPath } from './helpTopicsData';

import css from './HelpTopics.module.css';

const HelpTopicBlock = ({ block }) => {
  switch (block.type) {
    case 'heading':
      return <h4 className={css.blockHeading}>{block.text}</h4>;
    case 'paragraph':
      return <p className={css.blockParagraph}>{block.text}</p>;
    case 'image':
      return <img className={css.blockImage} src={block.url} alt={block.alt || ''} />;
    case 'video': {
      const title = block.title || 'Help video';
      // YouTube embed (same nocookie + rel=0 pattern as the rest of the app / Webflow).
      if (block.youtubeId) {
        return (
          <div className={css.blockVideoWrapper}>
            <iframe
              className={css.blockVideo}
              src={`https://www.youtube-nocookie.com/embed/${block.youtubeId}?rel=0`}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="HelpTopics.video.youtube"
            />
          </div>
        );
      }
      if (block.src) {
        return (
          <div className={css.blockVideoWrapper}>
            <video
              className={css.blockVideo}
              src={block.src}
              controls
              playsInline
              preload="metadata"
              title={title}
              data-testid="HelpTopics.video.file"
            />
          </div>
        );
      }
      return null;
    }
    default:
      return null;
  }
};

/**
 * Help tab content: topic list and per-topic pages from helpContent.json.
 * Opens directly on the topic mapped to the current URL path, if any.
 */
const HelpTopics = () => {
  const location = useLocation();
  const [selectedTopicId, setSelectedTopicId] = useState(
    () => getTopicByPath(location.pathname)?.id || null
  );
  const topic = getTopicById(selectedTopicId);

  if (topic) {
    return (
      <div className={css.root} data-testid="HelpTopics.topic">
        <button
          type="button"
          className={css.allTopicsLink}
          onClick={() => setSelectedTopicId(null)}
          data-testid="HelpTopics.allTopics"
        >
          <FormattedMessage id="HelpFlyout.allTopics" />
        </button>
        <h3 className={css.topicTitle}>{topic.title}</h3>
        {topic.blocks.map((block, index) => (
          <HelpTopicBlock key={index} block={block} />
        ))}
      </div>
    );
  }

  return (
    <div className={css.root} data-testid="HelpTopics.list">
      <h3 className={css.listTitle}>
        <FormattedMessage id="HelpFlyout.topicListTitle" />
      </h3>
      <ul className={css.topicList}>
        {getAllTopics().map(t => (
          <li key={t.id} className={css.topicListItem}>
            <button
              type="button"
              className={css.topicListButton}
              onClick={() => setSelectedTopicId(t.id)}
              data-testid={`HelpTopics.item.${t.id}`}
            >
              {t.title}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default HelpTopics;
